/**
 * Bewaart ontwerpen uit de ontwerptool in de browser, zodat een klant een
 * ontwerp vanuit de winkelmand opnieuw kan openen.
 *
 * Waarom in de browser en niet in de bestelling: een ontwerp bestaat uit
 * lagen, kleuren, posities en soms een geupload bestand van vele megabytes.
 * Dat past niet in een regeleigenschap, en het zou de bestelling voor de
 * drukker vol zetten met gegevens die hij niet nodig heeft. De winkelmand zelf
 * leeft ook per browser (via een cookie), dus het ontwerp bewaren op dezelfde
 * plek past bij hoe de klant zijn winkelmand gebruikt.
 *
 * Waarom IndexedDB en niet localStorage: localStorage houdt op bij een paar
 * megabyte en kan geen bestanden bewaren. Een foto van een telefoon is al
 * groter dan dat. IndexedDB bewaart het bestand zelf, en daardoor kan het ook
 * weer in een canvas getekend worden zonder dat de server dat moet toestaan.
 *
 * Een ontwerp dat niet (meer) gevonden wordt is geen fout: de winkelmand toont
 * dan gewoon geen knop om het aan te passen.
 */
(() => {
  if (window.FlagOntwerpOpslag) return;

  const DATABASE = 'ontwerpjevlag-ontwerpen';
  const TABEL = 'ontwerpen';
  const VERSIE = 1;
  // Een winkelmand blijft niet eeuwig liggen. Oude ontwerpen ruimen we op,
  // anders groeit de opslag bij iemand die vaak ontwerpt ongemerkt door.
  const BEWAARDAGEN = 60;

  let verbinding = null;

  const open = () => {
    if (verbinding) return verbinding;
    verbinding = new Promise((klaar, mislukt) => {
      if (!('indexedDB' in window)) {
        mislukt(new Error('IndexedDB is niet beschikbaar'));
        return;
      }
      const verzoek = indexedDB.open(DATABASE, VERSIE);
      verzoek.onupgradeneeded = () => {
        const db = verzoek.result;
        if (!db.objectStoreNames.contains(TABEL)) db.createObjectStore(TABEL, { keyPath: 'id' });
      };
      verzoek.onsuccess = () => klaar(verzoek.result);
      verzoek.onerror = () => mislukt(verzoek.error);
      verzoek.onblocked = () => mislukt(new Error('IndexedDB geblokkeerd'));
    }).catch((fout) => {
      verbinding = null;
      throw fout;
    });
    return verbinding;
  };

  /** Voert een handeling uit binnen een transactie en geeft de uitkomst terug. */
  const handeling = (modus, doe) =>
    open().then(
      (db) =>
        new Promise((klaar, mislukt) => {
          const tx = db.transaction(TABEL, modus);
          const tabel = tx.objectStore(TABEL);
          let uitkomst;
          const verzoek = doe(tabel);
          if (verzoek) verzoek.onsuccess = () => { uitkomst = verzoek.result; };
          tx.oncomplete = () => klaar(uitkomst);
          tx.onerror = () => mislukt(tx.error);
          tx.onabort = () => mislukt(tx.error);
        })
    );

  const haal = (id) => {
    if (!id) return Promise.resolve(null);
    return handeling('readonly', (t) => t.get(id)).then((r) => r || null).catch(() => null);
  };

  const bewaar = (ontwerp) =>
    handeling('readwrite', (t) => t.put(Object.assign({}, ontwerp, { bewaard: Date.now() })));

  window.FlagOntwerpOpslag = {
    haal,
    bewaar,

    bestaat: (id) => haal(id).then((r) => !!r),

    /** Verwijdert één ontwerp (een concept dat in de winkelmand is beland of dat de klant weggooit). */
    verwijder: (id) => (id ? handeling('readwrite', (t) => t.delete(id)).catch(() => {}) : Promise.resolve()),

    /** Kopieert een ontwerp onder een nieuwe sleutel. Geeft false als het origineel er niet is. */
    kopieer: async (id, nieuwId) => {
      const ontwerp = await haal(id);
      if (!ontwerp) return false;
      await bewaar(Object.assign({}, ontwerp, { id: nieuwId }));
      return true;
    },

    ruimOp: () =>
      handeling('readwrite', (t) => {
        const grens = Date.now() - BEWAARDAGEN * 864e5;
        const cursor = t.openCursor();
        cursor.onsuccess = () => {
          const c = cursor.result;
          if (!c) return;
          if ((c.value && c.value.bewaard ? c.value.bewaard : 0) < grens) c.delete();
          c.continue();
        };
      }).catch(() => {}),
  };

  // Scripts die eerder op de pagina staan (de ontwerptool) draaien voor deze
  // module. Laat ze weten dat de opslag er nu is.
  document.dispatchEvent(new Event('flagcfg:opslag-klaar'));

  // Opruimen mag rustig, als de pagina verder klaar is.
  const later = window.requestIdleCallback
    ? (fn) => window.requestIdleCallback(fn)
    : (fn) => setTimeout(fn, 4000);
  later(() => window.FlagOntwerpOpslag.ruimOp());
})();
